
import type { VersionConfig, AppPageMetadata } from './types';

/**
 * Version Registry
 * 
 * Central control for app versioning since package.json is read-only.
 * This serves as the single source of truth for version information.
 */

export const VERSION_REGISTRY: VersionConfig = {
  authentication: 'wip',
  database: 'wip', 
  components: 'wip',
  hooks: 'wip',
  utils: 'wip',
  services: 'wip',
  theming: 'stable',
  dashboard: 'wip',
  candidates: 'wip',
  jobs: 'wip',
  clients: 'wip',
  leads: 'wip',
  pipeline: 'wip',
  calendar: 'wip',
  analytics: 'wip',
  settings: 'wip',
  messaging: 'wip',
  automation: 'wip',
  aiHub: 'wip',
  betaProgram: 'wip',
  supabase: 'wip',
  ai: 'wip'
};

export const APP_PAGES_METADATA: Record<keyof Omit<VersionConfig, 'authentication' | 'database' | 'components' | 'hooks' | 'utils' | 'services' | 'theming' | 'supabase' | 'ai'>, AppPageMetadata> = {
  dashboard: {
    version: '2.2.0-wip',
    lastUpdated: '2025-06-11',
    features: ['Enhanced onboarding flow', 'Improved authentication redirects'],
    status: 'wip'
  },
  candidates: {
    version: '2.2.0-wip', 
    lastUpdated: '2025-06-11',
    features: ['Profile and organization setup improvements'],
    status: 'wip'
  },
  jobs: {
    version: '2.2.0-wip',
    lastUpdated: '2025-06-11', 
    features: ['Better error handling in job flows'],
    status: 'wip'
  },
  clients: {
    version: '2.2.0-wip',
    lastUpdated: '2025-06-11',
    features: ['Improved organization member handling'],
    status: 'wip'
  },
  leads: {
    version: '2.1.0-wip',
    lastUpdated: '2025-06-10',
    features: ['Lead management improvements'],
    status: 'wip'
  },
  pipeline: {
    version: '2.1.0-wip', 
    lastUpdated: '2025-06-10',
    features: ['Pipeline visualization enhancements'],
    status: 'wip'
  },
  calendar: {
    version: '2.1.0-wip',
    lastUpdated: '2025-06-10',
    features: ['Microsoft 365 integration'],
    status: 'wip'
  },
  analytics: {
    version: '2.1.0-wip',
    lastUpdated: '2025-06-10', 
    features: ['Performance analytics'],
    status: 'wip'
  },
  settings: {
    version: '2.2.0-wip',
    lastUpdated: '2025-06-11',
    features: ['Enhanced settings management', 'Version synchronization'],
    status: 'wip'
  },
  messaging: {
    version: '2.1.0-wip',
    lastUpdated: '2025-06-10',
    features: ['Email sync capabilities'],
    status: 'wip'
  },
  automation: {
    version: '2.1.0-wip',
    lastUpdated: '2025-06-10',
    features: ['Workflow automation'],
    status: 'wip'
  },
  aiHub: {
    version: '2.1.0-wip',
    lastUpdated: '2025-06-10',
    features: ['AI agent management'],
    status: 'wip'
  },
  betaProgram: {
    version: '2.1.0-wip',
    lastUpdated: '2025-06-10',
    features: ['Beta program management'],
    status: 'wip'
  }
};

export const VERSION_METADATA = {
  version: '2.2.0-wip',
  lastUpdated: '2025-06-11T00:00:00.000Z',
  baseline: 'wip',
  description: 'Enhanced onboarding flow with improved authentication handling, organization setup, and better error recovery. Conservative cleanup for edge cases and robust redirect logic.',
  notes: 'VERSION 2.2.0-wip: Major onboarding improvements. Establishes solid fallback point with improved user experience and authentication flow stability.',
  changelog: 'Enhanced onboarding wizard with better organization handling, improved authentication redirects, conservative cleanup system, and robust error handling for edge cases.',
  packageJsonReadOnly: true,
  internalVersioning: true
};

export const CRITICAL_FILES: Record<keyof VersionConfig, string[]> = {
  authentication: [
    'src/contexts/AuthContext.tsx',
    'src/features/auth/**/*',
    'src/hooks/useOnboarding*.ts'
  ],
  database: [
    'src/integrations/supabase/**/*',
    'supabase/**/*'
  ],
  components: [
    'src/components/**/*'
  ],
  hooks: [
    'src/hooks/**/*'
  ],
  utils: [
    'src/utils/**/*'
  ],
  services: [
    'src/services/**/*'
  ],
  theming: [
    'src/components/theme-provider.tsx',
    'src/components/layout/ThemeToggle.tsx',
    'src/index.css',
    'tailwind.config.ts'
  ],
  dashboard: [
    'src/pages/Dashboard.tsx',
    'src/components/dashboard/**/*'
  ],
  candidates: [
    'src/pages/Candidates.tsx',
    'src/components/candidates/**/*'
  ],
  jobs: [
    'src/pages/Jobs.tsx',
    'src/components/jobs/**/*'
  ],
  clients: [
    'src/pages/Clients.tsx',
    'src/components/clients/**/*'
  ],
  leads: [
    'src/pages/Leads.tsx',
    'src/components/leads/**/*'
  ],
  pipeline: [
    'src/pages/Pipeline.tsx',
    'src/components/candidates/pipeline/**/*'
  ],
  calendar: [
    'src/pages/Calendar.tsx',
    'src/components/calendar/**/*'
  ],
  analytics: [
    'src/pages/Analytics.tsx',
    'src/components/analytics/**/*'
  ],
  settings: [
    'src/pages/Settings.tsx',
    'src/components/settings/**/*'
  ],
  messaging: [
    'src/pages/Messages.tsx',
    'src/features/messages/**/*'
  ],
  automation: [
    'src/pages/Automation.tsx',
    'src/components/automation/**/*'
  ],
  aiHub: [
    'src/pages/AIHub.tsx',
    'src/components/ai/**/*'
  ],
  betaProgram: [
    'src/pages/BetaProgram.tsx',
    'src/components/beta-program/**/*'
  ],
  supabase: [
    'src/integrations/supabase/**/*'
  ],
  ai: [
    'src/components/ai/**/*',
    'src/hooks/ai/**/*'
  ]
};

export function getVersionStats() {
  const versions = Object.values(VERSION_REGISTRY);
  const stable = versions.filter(v => v === 'stable').length;
  const wip = versions.filter(v => v === 'wip').length;
  const legacy = versions.filter(v => v === 'legacy').length;
  const strict = versions.filter(v => v === 'strict').length;
  
  return { stable, wip, legacy, strict, total: versions.length };
}

export function rollbackToStable(): VersionConfig {
  return Object.keys(VERSION_REGISTRY).reduce((acc, key) => {
    acc[key as keyof VersionConfig] = 'stable';
    return acc;
  }, {} as VersionConfig);
}
